import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import {
  ArrowLeft, Stethoscope, Send, Square, Copy, Check, RotateCcw,
  ChevronDown, ShieldCheck, Sparkles, Mic, MicOff, FileText, FileDown, MessageCircle, Zap,
} from 'lucide-react';
import { API, getUserPhone } from '../lib/api';

const SPEECH_LANG = {
  English: 'en-IN', Hindi: 'hi-IN', Hinglish: 'hi-IN', Bengali: 'bn-IN', Marathi: 'mr-IN',
  Telugu: 'te-IN', Tamil: 'ta-IN', Gujarati: 'gu-IN', Kannada: 'kn-IN', Malayalam: 'ml-IN',
  Punjabi: 'pa-IN', Odia: 'or-IN', Assamese: 'as-IN', Urdu: 'ur-IN', Bhojpuri: 'hi-IN',
  Konkani: 'hi-IN', Nepali: 'ne-NP', Sanskrit: 'hi-IN',
};

const LANGUAGES = [
  'English', 'Hindi', 'Hinglish', 'Bengali', 'Marathi', 'Telugu',
  'Tamil', 'Gujarati', 'Kannada', 'Malayalam', 'Punjabi', 'Odia',
  'Assamese', 'Urdu', 'Bhojpuri', 'Konkani', 'Nepali', 'Sanskrit',
];

const TASK_TYPES = [
  { id: 'answer', label: 'Clinical Answer', prefix: '' },
  { id: 'article', label: 'Write an Article', prefix: 'Write a well-structured, publication-ready medical article (with title, introduction, sub-headed sections, evidence, and conclusion) on: ' },
  { id: 'summary', label: 'Medical Summary', prefix: 'Write a concise, structured medical summary (suitable for records/handover) on: ' },
  { id: 'operative', label: 'Operative Notes', prefix: 'Draft complete, print-ready operative notes (pre-op diagnosis, procedure, anaesthesia, findings, steps, blood loss, post-op plan) for: ' },
  { id: 'discharge', label: 'Discharge Summary', prefix: 'Draft a complete discharge summary (diagnosis, course, medications with doses, follow-up, red-flag advice) for: ' },
  { id: 'referral', label: 'Referral Letter', prefix: 'Write a professional NMC-compliant referral letter (with clinical summary, reason for referral, urgency) for: ' },
  { id: 'case-report', label: 'Case Report', prefix: 'Write a journal-style case report (abstract, introduction, case presentation, discussion with literature, conclusion) on: ' },
  { id: 'leaflet', label: 'Patient Education Leaflet', prefix: 'Create a simple, patient-friendly education leaflet (plain language, what/why/when to worry, FAQs) on: ' },
  { id: 'cme', label: 'CME Lecture / Talk', prefix: 'Prepare a structured CME lecture script with slide-by-slide outline, key teaching points and take-home messages on: ' },
  { id: 'abstract', label: 'Conference Abstract', prefix: 'Write a conference-ready structured abstract (background, methods, results, conclusion, ≤300 words) on: ' },
  { id: 'journal-club', label: 'Journal Club Review', prefix: 'Prepare a journal-club style critical appraisal (study design, methods critique, results, limitations, clinical bottom line) of: ' },
  { id: 'protocol', label: 'Research Protocol', prefix: 'Outline a research protocol (aim, objectives, design, sample size logic, methodology, outcomes, ethics) for: ' },
];

const SPECIALITIES = [
  { id: 'medicine', label: 'Internal Medicine', q: 'Approach to a 55-year-old with resistant hypertension — latest workup and drug sequence?' },
  { id: 'cardiology', label: 'Cardiology', q: 'NSTEMI management in the ED — latest ACC/AHA & ESC guideline-based approach?' },
  { id: 'pulmonology', label: 'Pulmonology', q: 'Acute COPD exacerbation — GOLD-based management and when to give antibiotics?' },
  { id: 'nephrology', label: 'Nephrology', q: 'CKD stage 3b with proteinuria — KDIGO-based BP targets and drug choices?' },
  { id: 'endocrine', label: 'Endocrinology', q: 'Newly diagnosed T2DM with obesity — first-line agents per latest ADA/EASD?' },
  { id: 'gastro', label: 'Gastroenterology', q: 'H. pylori eradication in India — current first-line regimen and duration?' },
  { id: 'neurology', label: 'Neurology', q: 'Acute ischaemic stroke — thrombolysis window and eligibility criteria?' },
  { id: 'paediatrics', label: 'Paediatrics', q: 'Febrile seizure in a 2-year-old — IAP-based evaluation and parental counselling?' },
  { id: 'surgery', label: 'General Surgery', q: 'Acute appendicitis — antibiotics vs surgery, current evidence?' },
  { id: 'ortho', label: 'Orthopaedics', q: 'Osteoporotic vertebral fracture in a post-menopausal woman — workup and treatment?' },
  { id: 'derm', label: 'Dermatology', q: 'Moderate-to-severe acne — stepwise treatment and when to use isotretinoin?' },
  { id: 'ent', label: 'ENT', q: 'Acute otitis media in adults — antibiotic choice and duration?' },
  { id: 'ophthal', label: 'Ophthalmology', q: 'Acute red eye — differentiating dangerous causes and red flags?' },
  { id: 'psychiatry', label: 'Psychiatry', q: 'First-episode major depression — first-line SSRI choice and follow-up plan?' },
  { id: 'critical', label: 'Critical Care', q: 'Septic shock resuscitation — latest Surviving Sepsis fluid and pressor targets?' },
  { id: 'anaesthesia', label: 'Anaesthesia', q: 'Difficult airway anticipated in an obese patient — pre-op assessment and airway plan per latest DAS/AIDAA guidelines?' },
  { id: 'infectious', label: 'Infectious Disease', q: 'Empirical antibiotics for community-acquired pneumonia in India?' },
  { id: 'onco', label: 'Oncology', q: 'Febrile neutropenia — risk stratification and empirical antibiotic choice?' },
  { id: 'urology', label: 'Urology', q: 'Acute ureteric colic — analgesia, imaging and MET indications?' },
  { id: 'plastic', label: 'Plastic Surgery', q: 'Deep partial-thickness burns of the hand — acute management and grafting decisions?' },
  { id: 'neurosurgery', label: 'Neurosurgery', q: 'Extradural haematoma on CT — surgical evacuation criteria and pre-op management?' },
  { id: 'ctvs', label: 'CTVS', q: 'Triple-vessel disease — CABG vs PCI decision as per latest revascularisation guidelines?' },
  { id: 'vascular', label: 'Vascular Surgery', q: 'Critical limb-threatening ischaemia — workup and revascularisation options?' },
  { id: 'paed-surgery', label: 'Paediatric Surgery', q: 'Infantile hypertrophic pyloric stenosis — diagnosis and pre-op preparation?' },
  { id: 'surg-onco', label: 'Surgical Oncology', q: 'Early breast cancer — breast conservation vs mastectomy and sentinel node approach?' },
  { id: 'medical-onco', label: 'Medical Oncology', q: 'Adjuvant chemotherapy in stage III colon cancer — current regimen and duration?' },
  { id: 'surg-gastro', label: 'Surgical Gastro', q: 'Choledocholithiasis — ERCP first vs lap CBD exploration, current recommendations?' },
  { id: 'hepatology', label: 'Hepatology', q: 'Decompensated cirrhosis with ascites — INASL-based management and SBP prophylaxis?' },
  { id: 'neonatology', label: 'Neonatology', q: 'Preterm 32-weeker with RDS — surfactant and respiratory support protocol?' },
  { id: 'cardio-interv', label: 'Interventional Cardiology', q: 'Primary PCI in STEMI — door-to-balloon targets and pharmacoinvasive strategy in India?' },
  { id: 'repro-med', label: 'Reproductive Medicine', q: 'Poor ovarian reserve — stimulation protocol options per latest IVF evidence?' },
  { id: 'endo-surgery', label: 'Endocrine Surgery', q: 'Papillary thyroid microcarcinoma — active surveillance vs surgery, current criteria?' },
  { id: 'pain', label: 'Pain Medicine', q: 'Chronic lumbar radicular pain — stepwise interventional options after failed conservative care?' },
  { id: 'geriatrics', label: 'Geriatrics', q: 'Polypharmacy in an 80-year-old — deprescribing approach using STOPP/START?' },
  { id: 'sports-med', label: 'Sports Medicine', q: 'ACL tear in a young athlete — reconstruction timing and rehab milestones?' },
  { id: 'nuclear-med', label: 'Nuclear Medicine', q: 'Differentiated thyroid cancer — radioiodine ablation indications and dosing?' },
];

const glass = 'bg-white/[0.03] backdrop-blur-2xl backdrop-saturate-150 border border-white/[0.08] rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]';

export default function GynovateMDPage() {
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [language, setLanguage] = useState('English');
  const [taskType, setTaskType] = useState('answer');
  const [output, setOutput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [copied, setCopied] = useState(false);
  const [lastQ, setLastQ] = useState('');
  const [evidence, setEvidence] = useState('');
  const [evidenceLoading, setEvidenceLoading] = useState(false);

  const fetchEvidence = async () => {
    const topic = lastQ || question;
    if (!topic.trim()) return;
    setEvidence('');
    setEvidenceLoading(true);
    try {
      const response = await fetch(`${API}/api/evidence/latest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic, language }),
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let full = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const d = line.slice(6).trim();
          if (d === '[DONE]') continue;
          try {
            const p = JSON.parse(d);
            if (p.type === 'chunk') { full += p.content; setEvidence(full); }
            else if (p.type === 'complete' && p.data?.content) { full = p.data.content; }
          } catch { /* partial */ }
        }
      }
      setEvidence(full);
    } catch {
      toast.error('Could not fetch latest evidence');
    }
    setEvidenceLoading(false);
  };
  const [isRecording, setIsRecording] = useState(false);
  const abortRef = useRef(null);
  const outputRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (streaming && outputRef.current) outputRef.current.scrollTop = outputRef.current.scrollHeight;
  }, [output, streaming]);

  const runQuestion = async (q) => {
    if (streaming || !q.trim()) return;
    setLastQ(q);
    setOutput('');
    setStreaming(true);
    setCopied(false);
    try {
      const sess = await axios.post(`${API}/api/sessions`, {
        mode: 'Gynovate MD',
        title: q.slice(0, 50),
        phone: getUserPhone(),
      });
      const controller = new AbortController();
      abortRef.current = controller;
      const res = await fetch(`${API}/api/sessions/${sess.data.id}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q, mode: 'Gynovate MD', language }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = '';
      let sseBuf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuf += decoder.decode(value, { stream: true });
        const lines = sseBuf.split('\n');
        sseBuf = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.type === 'chunk') { full += parsed.content; setOutput(full); }
            else if (parsed.type === 'error') throw new Error(parsed.message || 'AI error');
          } catch (e) { if (e.message && !e.message.includes('JSON')) throw e; }
        }
      }
    } catch (err) {
      if (err?.name === 'AbortError') toast.info('Stopped');
      else toast.error(err.message || 'Failed to answer');
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const buildQuery = () => {
    const t = TASK_TYPES.find((x) => x.id === taskType);
    const q = question.trim();
    return t?.prefix ? `${t.prefix}${q}` : q;
  };

  const stopGen = () => { try { abortRef.current?.abort(); } catch { /* noop */ } };

  const copyOutput = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(output);
      else {
        const ta = document.createElement('textarea');
        ta.value = output; document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      setCopied(true); toast.success('Copied'); setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Copy failed'); }
  };

  const toggleVoice = () => {
    if (isRecording) {
      try { recognitionRef.current?.stop(); } catch { /* noop */ }
      setIsRecording(false);
      return;
    }
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      toast.error('Voice input needs Chrome or Edge browser');
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const startRec = (lang, isRetry) => {
      const recognition = new SpeechRecognition();
      recognition.lang = lang;
      recognition.continuous = true;
      recognition.interimResults = true;
      const base = question ? question.trim() + ' ' : '';
      let finalText = '';
      recognition.onresult = (event) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const t = event.results[i][0].transcript;
          if (event.results[i].isFinal) finalText += t.trim() + ' ';
          else interim += t;
        }
        setQuestion((base + finalText + interim).replace(/\s+/g, ' ').trimStart());
      };
      recognition.onerror = (event) => {
        setIsRecording(false);
        if (event.error === 'language-not-supported' && !isRetry) {
          startRec('en-IN', true);
          return;
        }
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          toast.error('Microphone blocked — allow mic access in your browser settings');
        } else if (event.error === 'no-speech') {
          toast.info('No speech heard — tap the mic and speak');
        } else if (event.error !== 'aborted') {
          toast.error(`Voice error: ${event.error}`);
        }
      };
      recognition.onend = () => setIsRecording(false);
      recognitionRef.current = recognition;
      try {
        recognition.start();
        setIsRecording(true);
      } catch {
        toast.error('Could not start voice input');
      }
    };
    startRec(SPEECH_LANG[language] || 'en-IN', false);
  };

  const downloadWord = async () => {
    try {
      const filename = `gynovate_md_${new Date().toISOString().slice(0, 10)}.docx`;
      const res = await axios.post(
        `${API}/api/export/docx`,
        { content: output, title: 'Gynovate MD — Clinical Answer', mode: 'Gynovate MD', filename },
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Word document downloaded');
    } catch { toast.error('Word export failed'); }
  };

  const downloadPDF = () => {
    const win = window.open('', '_blank');
    if (!win) { toast.error('Pop-up blocked. Allow pop-ups and try again.'); return; }
    const html = output
      .replace(/^### (.*$)/gm, '<h3 style="color:#0f766e;margin:18px 0 8px;font-size:15px">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 style="color:#0f766e;margin:22px 0 10px;font-size:17px">$1</h2>')
      .replace(/^# (.*$)/gm, '<h1 style="color:#0f172a;margin:24px 0 12px;font-size:20px">$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/^> (.*$)/gm, '<blockquote style="border-left:3px solid #0f766e;margin:8px 0;padding:6px 12px;background:#f0fdfa;font-style:italic">$1</blockquote>')
      .replace(/^- (.*$)/gm, '<li style="margin:2px 0">$1</li>')
      .replace(/(<li.*<\/li>\n?)+/g, '<ul style="margin:6px 0 6px 16px;padding:0">$&</ul>')
      .replace(/^\|(.*)\|$/gm, (m) => '<tr>' + m.split('|').filter(c => c.trim()).map(c => `<td style="border:1px solid #e2e8f0;padding:6px 10px;font-size:13px">${c.trim()}</td>`).join('') + '</tr>')
      .replace(/(<tr>.*<\/tr>\n?)+/g, '<table style="border-collapse:collapse;width:100%;margin:10px 0">$&</table>')
      .replace(/^(?!<[hultrb])(.*\S.*)$/gm, '<p style="margin:4px 0;line-height:1.6">$1</p>');
    win.document.write(`<!DOCTYPE html><html><head><title>Gynovate MD — Clinical Answer</title>
<style>@media print{body{padding:20px !important}.no-print{display:none !important}@page{margin:1cm}}
body{font-family:'Segoe UI',Arial,sans-serif;padding:32px 40px;max-width:820px;margin:0 auto;color:#1e293b;font-size:14px;line-height:1.6}
.header{border-bottom:2px solid #0f766e;padding-bottom:12px;margin-bottom:20px}
.footer{margin-top:32px;border-top:1px solid #e2e8f0;padding-top:12px;color:#94a3b8;font-size:10px;text-align:center}</style></head><body>
<div class="header"><h2 style="margin:0 0 4px;font-size:18px;color:#0f172a">GYNOVATE MD&trade; — Clinical Answer</h2>
<p style="font-size:11px;color:#64748b;margin:2px 0">Date: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p></div>
<div class="content">${html}</div>
<div class="footer">Generated by GYNOVATE MD&trade; | Latest Indian guidelines-based clinical decision support — verify with clinical judgement.</div>
</body></html>`);
    win.document.close();
    win.print();
  };

  const shareWhatsApp = () => {
    const body = `*Gynovate MD — Clinical Answer*\n\n_Q: ${lastQ}_\n\n${output}`.slice(0, 3500);
    window.open(`https://wa.me/?text=${encodeURIComponent(body)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen w-full relative overflow-hidden text-slate-200 bg-[#0B0F19]" data-testid="gynovate-md-page">
      {/* Layered ambient background */}
      <div className="pointer-events-none fixed inset-0" aria-hidden="true">
        <div
          className="absolute inset-0 opacity-20 mix-blend-screen bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1758073519996-6d3c63b4922c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920')" }}
        />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at top, rgba(19,78,74,0.45) 0%, rgba(11,15,25,0.2) 45%, #0B0F19 80%)' }} />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] h-[420px] rounded-full opacity-25 blur-3xl" style={{ background: 'radial-gradient(circle, #0ea5a4 0%, transparent 65%)' }} />
        <div className="absolute bottom-0 right-0 w-[460px] h-[460px] rounded-full opacity-15 blur-3xl" style={{ background: 'radial-gradient(circle, #38bdf8 0%, transparent 65%)' }} />
        <div className="absolute inset-0 opacity-[0.07] mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-6 z-10">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-10">
          <button
            onClick={() => navigate('/chat')}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-semibold text-white/70 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
            data-testid="md-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Copilot
          </button>
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-teal-400/15 border border-teal-300/30 flex items-center justify-center">
              <Stethoscope className="w-4 h-4 text-teal-300" />
            </span>
            <span className="font-accent text-lg tracking-wide text-white/90">Gynovate&nbsp;<span className="italic text-teal-300 pr-1">MD</span></span>
          </div>
        </div>

        {/* Hero */}
        <div className="text-center mb-14 pt-8">
          <span className="relative inline-flex w-20 h-20 mx-auto mb-6 rounded-3xl bg-teal-400/10 border border-teal-300/25 items-center justify-center shadow-[0_10px_50px_rgba(45,212,191,0.3)]">
            <span className="absolute inset-0 rounded-3xl animate-aura-pulse bg-teal-400/10" />
            <Stethoscope className="w-9 h-9 text-teal-300 relative" strokeWidth={1.5} data-testid="md-logo" />
          </span>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-teal-300/70 mb-4">All Specialities · One Copilot</p>
          <h1 className="font-accent text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-5 leading-[1.12] text-white">
            Gynovate{' '}
            <span className="italic bg-clip-text text-transparent inline-block pr-2 font-medium" style={{ backgroundImage: 'linear-gradient(90deg, #5eead4, #38bdf8, #2DD4BF)' }}>MD</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-300/80 max-w-2xl mx-auto leading-relaxed" data-testid="md-tagline">
            Evidence-based clinical answers for <span className="text-teal-200 font-semibold">every speciality</span> — treatment as per the latest Indian guidelines.
          </p>
          <div className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-full bg-black/40 backdrop-blur-md border border-white/10" data-testid="md-guideline-badge">
            <ShieldCheck className="w-4 h-4 text-teal-300" strokeWidth={1.5} />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-white/70">Latest Indian guidelines · ICMR · NLEM · API · IAP · FOGSI · + International</span>
          </div>
        </div>

        {/* Ask box */}
        <div className={`${glass} p-6 sm:p-8`} data-testid="md-composer">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-teal-300" />
            <h2 className="text-lg md:text-xl font-bold tracking-wide">Ask any medical question</h2>
          </div>
          <div className="relative mb-4">
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) runQuestion(buildQuery()); }}
              rows={3}
              placeholder="e.g. Best first-line antibiotic for community-acquired pneumonia in an Indian adult? (Ctrl/⌘+Enter to ask)"
              data-testid="md-question-input"
              className="w-full px-4 py-3.5 pr-14 rounded-xl bg-white/5 border border-white/15 text-base text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-teal-400/60 focus:border-teal-400/60 resize-y"
            />
            <button
              onClick={toggleVoice}
              data-testid="md-voice-btn"
              title={isRecording ? 'Stop listening' : 'Ask by voice'}
              className={`absolute right-3 top-3.5 w-10 h-10 rounded-xl flex items-center justify-center border focus-visible:ring-2 focus-visible:ring-teal-500/50 transition-colors ${
                isRecording
                  ? 'bg-red-500/20 border-red-400/50 text-red-300'
                  : 'bg-white/5 border-white/15 text-teal-300 hover:bg-teal-400/10 hover:border-teal-300/40'
              }`}
            >
              {isRecording && <span className="absolute inset-0 rounded-xl animate-aura-pulse bg-red-500/25" />}
              {isRecording ? <MicOff className="w-5 h-5 relative" strokeWidth={1.5} /> : <Mic className="w-5 h-5 relative" strokeWidth={1.5} />}
            </button>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative shrink-0">
              <select
                value={taskType}
                onChange={(e) => setTaskType(e.target.value)}
                data-testid="md-task-select"
                className="w-full sm:w-56 appearance-none px-4 py-3.5 pr-10 rounded-xl bg-slate-900 border border-white/[0.12] text-base font-semibold text-white focus:outline-none focus:ring-2 focus:ring-teal-400/60 cursor-pointer [&>option]:bg-white [&>option]:text-slate-900"
                title="What do you want to create?"
              >
                {TASK_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <ChevronDown className="w-4 h-4 text-teal-300 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            <div className="relative shrink-0">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                data-testid="md-language-select"
                className="w-full sm:w-40 appearance-none px-4 py-3.5 pr-10 rounded-xl bg-slate-900 border border-white/[0.12] text-base font-semibold text-white focus:outline-none focus:ring-2 focus:ring-teal-400/60 cursor-pointer [&>option]:bg-white [&>option]:text-slate-900"
              >
                {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <ChevronDown className="w-4 h-4 text-teal-300 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            {streaming ? (
              <button
                onClick={stopGen}
                data-testid="md-stop-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-white/10 border border-white/20 text-base font-bold hover:bg-white/15 transition-colors"
              >
                <Square className="w-4 h-4 fill-current" /> Stop
              </button>
            ) : (
              <button
                onClick={() => runQuestion(buildQuery())}
                disabled={!question.trim()}
                data-testid="md-ask-btn"
                className="relative overflow-hidden flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-xl text-base font-bold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-opacity hover:opacity-95 shadow-[0_8px_30px_rgba(14,165,164,0.45)] focus-visible:ring-2 focus-visible:ring-teal-500/50 group"
                style={{ background: 'linear-gradient(135deg, #0ea5a4, #0369a1)' }}
              >
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent group-hover:translate-x-full transition-transform duration-700 ease-out pointer-events-none" />
                <Send className="w-4 h-4 relative" strokeWidth={1.5} /> <span className="relative">{taskType === 'answer' ? 'Get answer' : 'Create'}</span>
              </button>
            )}
          </div>
        </div>

        {/* Speciality quick-pick */}
        <div className="mt-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/45 mb-3">Or pick a speciality to start</p>
          <div className="flex flex-wrap gap-2" data-testid="md-specialities">
            {SPECIALITIES.map((s) => (
              <button
                key={s.id}
                onClick={() => { setQuestion(s.q); runQuestion(s.q); }}
                disabled={streaming}
                data-testid={`md-spec-${s.id}`}
                className="px-3.5 py-2 rounded-full text-sm font-semibold text-slate-300/80 bg-white/[0.04] border border-white/[0.08] hover:text-white hover:bg-teal-400/10 hover:border-teal-300/40 hover:-translate-y-[2px] hover:shadow-lg transition-transform duration-200 disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-teal-500/50"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Output */}
        {(output || streaming) && (
          <div className={`${glass} mt-8 overflow-hidden animate-fade-in`} data-testid="md-output">
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Sparkles className={`w-5 h-5 text-teal-300 ${streaming ? 'animate-pulse' : ''}`} />
                <span className="text-base font-bold">{streaming ? 'Thinking…' : 'Answer'}</span>
              </div>
              {!streaming && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => runQuestion(lastQ)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white/70 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                    data-testid="md-regenerate-btn"
                  >
                    <RotateCcw className="w-4 h-4" /> <span className="hidden sm:inline">Regenerate</span>
                  </button>
                  <button
                    onClick={downloadWord}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white/80 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                    data-testid="md-word-btn"
                    title="Download as Word (.docx)"
                  >
                    <FileText className="w-4 h-4 text-sky-300" /> Word
                  </button>
                  <button
                    onClick={downloadPDF}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white/80 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                    data-testid="md-pdf-btn"
                    title="Download / print as PDF"
                  >
                    <FileDown className="w-4 h-4 text-rose-300" /> PDF
                  </button>
                  <button
                    onClick={fetchEvidence}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
                    style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}
                    data-testid="md-evidence-btn"
                    title="Fetch the newest 2025–26 PubMed literature on this topic"
                  >
                    <Zap className="w-4 h-4" /> <span className="hidden sm:inline">Latest Evidence</span><span className="sm:hidden">2026</span>
                  </button>
                  <button
                    onClick={shareWhatsApp}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
                    style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}
                    data-testid="md-whatsapp-btn"
                    title="Share on WhatsApp"
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </button>
                  <button
                    onClick={copyOutput}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-bold text-white transition-opacity hover:opacity-90"
                    style={{ background: 'linear-gradient(135deg, #0ea5a4, #0369a1)' }}
                    data-testid="md-copy-btn"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              )}
            </div>
            <div ref={outputRef} className="px-6 sm:px-8 py-6 max-h-[65vh] overflow-y-auto">
              <div className="text-[15px] leading-[1.75]" style={{ fontFamily: "'Public Sans', system-ui, sans-serif" }}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: (props) => <h1 className="font-accent text-2xl sm:text-3xl font-semibold text-white mt-2 mb-4 tracking-tight" {...props} />,
                    h2: (props) => <h2 className="text-lg font-bold text-teal-200 mt-6 mb-2" {...props} />,
                    h3: (props) => <h3 className="text-base font-bold text-teal-200 mt-4 mb-2" {...props} />,
                    p: (props) => <p className="text-white/85 mb-4" {...props} />,
                    strong: (props) => <strong className="text-white font-bold" {...props} />,
                    em: (props) => <em className="text-white/70 italic" {...props} />,
                    ul: (props) => <ul className="list-disc pl-6 mb-4 space-y-2 text-white/85 marker:text-teal-400" {...props} />,
                    ol: (props) => <ol className="list-decimal pl-6 mb-4 space-y-2 text-white/85 marker:text-teal-300" {...props} />,
                    li: (props) => <li className="text-white/85 pl-1" {...props} />,
                    a: (props) => <a className="text-teal-200 underline underline-offset-2 hover:text-white" target="_blank" rel="noopener noreferrer" {...props} />,
                    table: (props) => <div className="overflow-x-auto mb-4 rounded-xl border border-white/10"><table className="w-full text-sm border-collapse" {...props} /></div>,
                    thead: (props) => <thead className="bg-white/[0.07]" {...props} />,
                    th: (props) => <th className="px-3.5 py-2.5 text-left font-bold text-teal-200 border-b border-white/10" {...props} />,
                    td: (props) => <td className="px-3.5 py-2.5 text-white/80 border-b border-white/5" {...props} />,
                    code: (props) => <code className="bg-teal-500/20 rounded px-1.5 py-0.5 text-teal-100 text-[13px] font-mono" {...props} />,
                    hr: () => <hr className="border-white/10 my-6" />,
                    blockquote: (props) => <blockquote className="border-l-2 border-teal-400 bg-white/[0.03] rounded-r-lg pl-4 pr-3 py-2 text-white/75 italic mb-4" {...props} />,
                  }}
                >
                  {output || '…'}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        )}

        {(evidence || evidenceLoading) && (
          <div className={`${glass} mt-5 overflow-hidden animate-fade-in`} data-testid="md-evidence-output">
            <div className="flex items-center gap-2 px-6 py-4 border-b border-white/10">
              <Zap className={`w-5 h-5 text-indigo-300 ${evidenceLoading ? 'animate-pulse' : ''}`} />
              <span className="text-base font-bold">Latest Evidence · Live PubMed</span>
            </div>
            <div className="px-6 sm:px-8 py-6 max-h-[60vh] overflow-y-auto">
              <div className="text-[15px] leading-[1.75]" style={{ fontFamily: "'Public Sans', system-ui, sans-serif" }}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h2: (props) => <h2 className="text-lg font-bold text-indigo-200 mt-5 mb-2" {...props} />,
                    h3: (props) => <h3 className="text-base font-bold text-indigo-200 mt-4 mb-2" {...props} />,
                    p: (props) => <p className="text-white/85 mb-3" {...props} />,
                    strong: (props) => <strong className="text-white font-bold" {...props} />,
                    ul: (props) => <ul className="list-disc pl-6 mb-3 space-y-1.5 text-white/85 marker:text-indigo-300" {...props} />,
                    ol: (props) => <ol className="list-decimal pl-6 mb-3 space-y-1.5 text-white/85 marker:text-indigo-300" {...props} />,
                    a: (props) => <a className="text-indigo-200 underline underline-offset-2 hover:text-white" target="_blank" rel="noopener noreferrer" {...props} />,
                  }}
                >
                  {evidence || '…'}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        )}

        <p className="text-center text-xs text-white/30 mt-10 pb-6">
          Clinical decision support — verify against local protocols and your clinical judgement.
        </p>
      </div>
    </div>
  );
}
